import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import Select from "react-select";
import {useAtom} from "jotai";
import styled from "styled-components";

import {
  BoardSearchDetail,
  BoardTableContainer
} from "../../assets/GlobalStyles";
import Table from "../../components/table";
import {
  dataTotalInfo
} from "../../components/common/entity";
import {
  selUserList
} from "../../services/Platform/ManageUserAxios";
import {
  adverType,
  columnUserData,
  searchAccountInfo,
  selectAccountUseInfo,
  selectKeywordType,
  userInfoAtom
} from "./entity/User";
import UserTable from "../../components/table/UserTable";

const dashboardSelectStyle = {
  control: (
      provided,
      state
  ) => ({
    ...provided,
    minHeight: 40,
    height: 40,
    borderColor: state.isFocused
        ? "#f5811f"
        : "#e5e5e5",
    borderRadius: 6,
    backgroundColor:
        state.isDisabled
            ? "#f5f5f5"
            : "#fff",
    boxShadow: "none",
    fontSize: 13,
    cursor:
        state.isDisabled
            ? "not-allowed"
            : "pointer",
    "&:hover": {
      borderColor:
          state.isDisabled
              ? "#e5e5e5"
              : "#f1d4c2"
    }
  }),
  valueContainer: provided => ({
    ...provided,
    height: 40,
    padding: "0 11px"
  }),
  input: provided => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  indicatorsContainer:
      provided => ({
        ...provided,
        height: 40
      }),
  indicatorSeparator: () => ({
    display: "none"
  }),
  dropdownIndicator:
      provided => ({
        ...provided,
        padding: "0 9px",
        color: "#888"
      }),
  singleValue: provided => ({
    ...provided,
    color: "#444"
  }),
  placeholder: provided => ({
    ...provided,
    color: "#aaa"
  }),
  menu: provided => ({
    ...provided,
    zIndex: 30,
    overflow: "hidden",
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    boxShadow:
        "0 8px 24px rgba(0, 0, 0, 0.1)"
  }),
  option: (
      provided,
      state
  ) => ({
    ...provided,
    padding: "9px 11px",
    backgroundColor:
        state.isSelected
            ? "#fff4e8"
            : state.isFocused
                ? "#fffaf6"
                : "#fff",
    color:
        state.isSelected
            ? "#f5811f"
            : "#555",
    fontSize: 13,
    cursor: "pointer"
  })
};

function getOptionValue(option) {
  if (
      option &&
      typeof option === "object"
  ) {
    return option.value;
  }

  return option ?? "";
}

function findSelectedOption(
    options,
    value
) {
  const rawValue =
      getOptionValue(value);

  return (
      options.find(
          option =>
              option.value === rawValue
      ) ??
      options[0] ??
      null
  );
}

export default function UserManage() {
  const [
    searchAccountInfoState,
    setSearchAccountInfoState
  ] = useState({
    ...searchAccountInfo
  });

  const [
    userInfoList,
    setUserInfoList
  ] = useAtom(userInfoAtom);

  const [
    totalInfo,
    setTotalInfo
  ] = useState(dataTotalInfo);

  const [
    isSearching,
    setIsSearching
  ] = useState(false);

  const [
    isOpen,
    setIsOpen
  ] = useState(false);

  const adverTypeOptions =
      useMemo(
          () => adverType ?? [],
          []
      );

  const accountUseOptions =
      useMemo(
          () =>
              selectAccountUseInfo ?? [],
          []
      );

  const searchTypeOptions =
      useMemo(
          () =>
              selectKeywordType ?? [],
          []
      );

  const selectedAdverType =
      findSelectedOption(
          adverTypeOptions,
          searchAccountInfoState
              .adverType
      );

  const selectedAccountState =
      findSelectedOption(
          accountUseOptions,
          searchAccountInfoState
              .accountStateType
      );

  const selectedSearchType =
      findSelectedOption(
          searchTypeOptions,
          searchAccountInfoState
              .searchType
      );

  const isKeywordDisabled =
      selectedSearchType?.value ===
      "DEFAULT";

  const currentUsers =
      Array.isArray(userInfoList)
          ? userInfoList
          : [];

  const loadUserList =
      async params => {
        if (isSearching) {
          return;
        }

        setIsSearching(true);

        try {
          const response =
              await selUserList({
                ...params,
                accountStateType:
                    getOptionValue(
                        params
                            .accountStateType
                    ),
                adverType:
                    getOptionValue(
                        params.adverType
                    ),
                searchType:
                    getOptionValue(
                        params.searchType
                    ),
                keyword:
                    params.keyword
                    ?.trim() ?? ""
              });

          if (!response) {
            setUserInfoList([]);
            setTotalInfo({
              ...dataTotalInfo,
              totalCount: 0
            });
            return;
          }

          setUserInfoList(
              Array.isArray(
                  response.content
              )
                  ? response.content
                  : []
          );

          setTotalInfo({
            totalCount:
                response.totalElements ??
                0,
            totalPages:
                response.totalPages ??
                0,
            currentPage:
                response.pageNumber ??
                0
          });
        } catch (error) {
          console.error(
              "사용자 목록 조회 실패:",
              error
          );

          setUserInfoList([]);
          setTotalInfo({
            ...dataTotalInfo,
            totalCount: 0
          });
        } finally {
          setIsSearching(false);
        }
      };

  useEffect(() => {
    loadUserList(
        searchAccountInfoState
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateSearchState =
      nextValues => {
        setSearchAccountInfoState(
            prev => ({
              ...prev,
              ...nextValues
            })
        );
      };

  const handleAdverType =
      selectedOption => {
        updateSearchState({
          adverType:
          selectedOption
        });
      };

  const handleAccountState =
      selectedOption => {
        updateSearchState({
          accountStateType:
          selectedOption
        });
      };

  const handleSearchType =
      selectedOption => {
        updateSearchState({
          searchType:
          selectedOption,
          keyword:
              selectedOption?.value ===
              "DEFAULT"
                  ? ""
                  : searchAccountInfoState
                      .keyword
        });
      };

  const handleKeyword =
      event => {
        updateSearchState({
          keyword:
          event.target.value
        });
      };

  const handleSearch = async event => {
    event?.preventDefault();

    await loadUserList(
        searchAccountInfoState
    );
  };

  const handleReset = async () => {
    const resetState = {
      ...searchAccountInfo,
      keyword: ""
    };

    setSearchAccountInfoState(
        resetState
    );

    await loadUserList(
        resetState
    );
  };

  const selectedAdverLabel =
      selectedAdverType?.label ??
      selectedAdverType?.name ??
      "전체 광고주";

  const selectedAccountLabel =
      selectedAccountState?.label ??
      selectedAccountState?.name ??
      "전체 상태";

  const selectedSearchLabel =
      selectedSearchType?.label ??
      selectedSearchType?.name ??
      "전체 검색";

  const keyword =
      searchAccountInfoState
      .keyword?.trim() ?? "";

  return (
      <>
        <UserManageCards>
          <BoardSearchDetail style={{paddingTop: '20px'}}>
            <ConditionPanel
                $open={isOpen}
            >
              <ConditionToggleButton
                  role="button"
                  tabIndex={0}
                  $open={isOpen}
                  aria-expanded={isOpen}
                  aria-controls="user-manage-condition-body"
                  onClick={() =>
                      setIsOpen(
                          prev => !prev
                      )
                  }
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setIsOpen(prev => !prev);
                    }
                  }}
              >
                <HeaderTitleArea>
                  <HeaderAccent />

                  <div>
                    <HeaderTitle>
                      조회 조건
                    </HeaderTitle>

                    <HeaderDescription>
                      광고주 구분, 사용 여부와
                      사용자 검색 조건을 기준으로
                      목록을 조회합니다.
                    </HeaderDescription>
                  </div>
                </HeaderTitleArea>

                <ConditionHeaderRight>
                  {!isOpen && (
                      <ConditionSummary>
                        <SummaryChip>
                          {selectedAdverLabel}
                        </SummaryChip>

                        <SummaryChip>
                          {selectedAccountLabel}
                        </SummaryChip>

                        <SummaryChip>
                          {selectedSearchLabel}
                        </SummaryChip>

                        {keyword && (
                            <SummaryChip
                                $accent
                            >
                              {keyword}
                            </SummaryChip>
                        )}
                      </ConditionSummary>
                  )}

                  <DropdownIcon
                      $open={isOpen}
                      aria-hidden="true"
                  >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                    >
                      <path
                          fill="currentColor"
                          d="m7 10 5 5 5-5H7Z"
                      />
                    </svg>
                  </DropdownIcon>
                </ConditionHeaderRight>
              </ConditionToggleButton>

              {isOpen && (
                  <ConditionBody
                      id="user-manage-condition-body"
                  >
                    <FilterGrid>
                      <FilterItem>
                        <FilterLabel>
                          광고주 구분
                        </FilterLabel>

                        <Select
                            options={
                              adverTypeOptions
                            }
                            value={
                              selectedAdverType
                            }
                            onChange={
                              handleAdverType
                            }
                            styles={
                              dashboardSelectStyle
                            }
                            isSearchable={false}
                        />
                      </FilterItem>

                      <FilterItem>
                        <FilterLabel>
                          사용 여부
                        </FilterLabel>

                        <Select
                            options={
                              accountUseOptions
                            }
                            value={
                              selectedAccountState
                            }
                            onChange={
                              handleAccountState
                            }
                            styles={
                              dashboardSelectStyle
                            }
                            isSearchable={false}
                        />
                      </FilterItem>

                      <SearchFilterItem>
                        <FilterLabel>
                          사용자 검색
                        </FilterLabel>

                        <SearchControlRow>
                          <SearchTypeWrap>
                            <Select
                                options={
                                  searchTypeOptions
                                }
                                value={
                                  selectedSearchType
                                }
                                onChange={
                                  handleSearchType
                                }
                                styles={
                                  dashboardSelectStyle
                                }
                                isSearchable={false}
                            />
                          </SearchTypeWrap>

                          <KeywordInputWrap>
                            <SearchIcon
                                aria-hidden="true"
                            >
                              <svg
                                  width="15"
                                  height="15"
                                  viewBox="0 0 24 24"
                              >
                                <path
                                    fill="currentColor"
                                    d="m21 19-4.35-4.35a7.5 7.5 0 1 0-2 2L19 21l2-2ZM5 10.5a5.5 5.5 0 1 1 11 0 5.5 5.5 0 0 1-11 0Z"
                                />
                              </svg>
                            </SearchIcon>

                            <KeywordInput
                                type="text"
                                placeholder={
                                  isKeywordDisabled
                                      ? "검색 유형을 선택해주세요."
                                      : "아이디 및 담당자명 검색"
                                }
                                value={
                                    searchAccountInfoState
                                        .keyword ?? ""
                                }
                                disabled={
                                    isKeywordDisabled ||
                                    isSearching
                                }
                                onChange={
                                  handleKeyword
                                }
                                onKeyDown={event => {
                                  if (
                                      event.key ===
                                      "Enter"
                                  ) {
                                    handleSearch(
                                        event
                                    );
                                  }
                                }}
                            />

                            {keyword && (
                                <ClearButton
                                    type="button"
                                    aria-label="검색어 지우기"
                                    disabled={
                                      isSearching
                                    }
                                    onClick={() =>
                                        updateSearchState({
                                          keyword: ""
                                        })
                                    }
                                >
                                  <span />
                                </ClearButton>
                            )}
                          </KeywordInputWrap>
                        </SearchControlRow>
                      </SearchFilterItem>
                    </FilterGrid>

                    <ConditionFooter>
                      <ConditionGuide>
                        조건을 설정한 뒤 적용 버튼을
                        눌러 사용자 목록을 조회합니다.
                      </ConditionGuide>

                      <ConditionActions>
                        <ResetButton
                            type="button"
                            disabled={isSearching}
                            onClick={handleReset}
                        >
                          초기화
                        </ResetButton>

                        <ApplyButton
                            type="button"
                            disabled={isSearching}
                            onClick={handleSearch}
                        >
                          {isSearching && <ButtonSpinner aria-hidden="true" />}
                          {isSearching ? "조회 중" : "적용"}
                        </ApplyButton>
                      </ConditionActions>
                    </ConditionFooter>
                  </ConditionBody>
              )}
            </ConditionPanel>
          </BoardSearchDetail>
          <BoardTableContainer>
            <ResultCard>
              <ResultHeader>
                <ResultTitleArea>
                  <ResultAccent />

                  <div>
                    <ResultTitleLine>
                      <ResultTitle>사용자 목록</ResultTitle>
                      <ResultCount>
                        <strong>{totalInfo.totalCount ?? 0}</strong>
                        <span>명</span>
                      </ResultCount>
                    </ResultTitleLine>

                    <ResultDescription>
                      등록된 사용자 정보와
                      계정 상태를 확인합니다.
                    </ResultDescription>
                  </div>
                </ResultTitleArea>

              </ResultHeader>

              <TableResultBox>
                {isSearching && (
                    <LoadingOverlay>
                      <LoadingSpinner />

                      <span>
                        사용자 목록을
                        불러오고 있습니다.
                      </span>
                    </LoadingOverlay>
                )}

                <UserTable
                    columns={
                      columnUserData
                    }
                    totalCount={[
                      totalInfo.totalCount,
                      "사용자"
                    ]}
                    downloadList={false}
                    data={currentUsers}
                    idProperty="id"
                    defaultSortInfo={{
                      name: "status",
                      dir: 1
                    }}
                    emptyText={
                      isSearching
                          ? "사용자 목록을 조회하고 있습니다."
                          : "조회된 사용자가 없습니다."
                    }
                    rowPrimaryAction={true}
                    rowPrimaryActionSelector={
                      '[data-row-primary-action="true"], a[href]'
                    }
                />
              </TableResultBox>
            </ResultCard>
          </BoardTableContainer>
        </UserManageCards>
      </>
  );
}

const UserManageCards = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 16px;

  ${BoardSearchDetail},
  ${BoardTableContainer} {
    margin: 0;
    padding: 0 !important;
    border: 0 !important;
    border-top: 0 !important;
    border-bottom: 0 !important;
    background: transparent;
  }
`;

const ConditionPanel = styled.section`
  position: relative;
  z-index: ${({$open}) => $open ? 20 : 1};
  overflow: ${({$open}) => $open ? "visible" : "hidden"};
  border: 1px solid
  ${({$open}) => $open ? "#f1d4c2" : "#e5e8eb"};
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
`;

const ConditionToggleButton =
    styled.div`
      display: flex;
      width: 100%;
      min-height: 66px;
      align-items: center;
      justify-content: space-between;
      gap: 18px;
      padding: 14px 18px;
      border: 0;
      border-radius: 10px;
      border-bottom: 1px solid
      ${({$open}) =>
              $open
                      ? "#f1e4da"
                      : "transparent"};
      background-color: #fff;
      color: inherit;
      text-align: left;
      cursor: pointer;

      &:hover {
        background-color: #fffaf6;
      }

      &:focus-visible {
        outline: 1px solid #f5811f;
        outline-offset: -1px;
      }

      @media (max-width: 900px) {
        align-items: flex-start;
        flex-direction: column;
        gap: 10px;
      }
    `;

const HeaderTitleArea = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
`;

const HeaderAccent = styled.span`
  width: 4px;
  height: 34px;
  flex: 0 0 4px;
  border-radius: 2px;
  background-color: #f5811f;
`;

const HeaderTitle = styled.div`
  margin-bottom: 4px;
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const HeaderDescription = styled.div`
  color: #888;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
`;

const ConditionHeaderRight =
    styled.span`
      display: flex;
      min-width: 0;
      align-items: center;
      justify-content: flex-end;
      gap: 9px;

      @media (max-width: 900px) {
        width: 100%;
        justify-content: space-between;
      }
    `;

const ConditionSummary = styled.span`
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  flex-wrap: wrap;

  @media (max-width: 700px) {
    justify-content: flex-start;
  }
`;

const SummaryChip = styled.span`
  display: inline-flex;
  max-width: 190px;
  min-height: 25px;
  align-items: center;
  padding: 0 9px;
  overflow: hidden;
  border: 1px solid
  ${({$accent}) =>
          $accent
                  ? "#f1d4c2"
                  : "#e5e5e5"};
  border-radius: 13px;
  background-color:
          ${({$accent}) =>
                  $accent
                          ? "#fff4e8"
                          : "#fff"};
  color:
          ${({$accent}) =>
                  $accent
                          ? "#f5811f"
                          : "#777"};
  font-size: 11px;
  font-weight:
          ${({$accent}) =>
                  $accent
                          ? 700
                          : 500};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DropdownIcon = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #f5811f;
  transform:
          rotate(
                  ${({$open}) =>
                          $open
                                  ? "180deg"
                                  : "0deg"}
          );
  transition: transform 0.18s ease;
`;

const ConditionBody = styled.div`
  min-height: 190px;
  box-sizing: border-box;
  padding: 22px 20px 28px;
  animation:
          user-condition-open
          0.18s ease-out;

  @keyframes user-condition-open {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 700px) {
    min-height: 0;
    padding: 18px 15px 22px;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(180px, 0.65fr)
    minmax(180px, 0.65fr)
    minmax(420px, 1.7fr);
  gap: 18px;

  @media (max-width: 1100px) {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FilterItem = styled.div`
  min-width: 0;
`;

const SearchFilterItem = styled.div`
  min-width: 0;

  @media (max-width: 1100px) {
    grid-column: 1 / -1;
  }

  @media (max-width: 720px) {
    grid-column: auto;
  }
`;

const FilterLabel = styled.div`
  margin-bottom: 8px;
  color: #555;
  font-size: 13px;
  font-weight: 700;
`;

const SearchControlRow = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 8px;

  @media (max-width: 620px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const SearchTypeWrap = styled.div`
  width: 150px;
  flex: 0 0 150px;

  @media (max-width: 620px) {
    width: 100%;
    flex-basis: auto;
  }
`;

const KeywordInputWrap = styled.div`
  position: relative;
  min-width: 0;
  flex: 1;
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 2;
  display: inline-flex;
  color: #999;
  pointer-events: none;
  transform: translateY(-50%);
`;

const KeywordInput = styled.input`
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  padding: 0 39px 0 37px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  background-color: #fff;
  color: #444;
  font-size: 13px;
  outline: none;

  &:hover:not(:disabled) {
    border-color: #f1d4c2;
  }

  &:focus {
    border-color: #f5811f;
  }

  &:disabled {
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  top: 50%;
  right: 9px;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: #f1f1f1;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 9px;
    height: 1px;
    background-color: #888;
    content: "";
  }

  &::before {
    transform:
            translate(-50%, -50%)
            rotate(45deg);
  }

  &::after {
    transform:
            translate(-50%, -50%)
            rotate(-45deg);
  }

  &:hover:not(:disabled) {
    background-color: #fff4e8;
  }

  &:hover:not(:disabled)::before,
  &:hover:not(:disabled)::after {
    background-color: #f5811f;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  & > span {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
  }
`;

const ConditionFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 16px;
  
  @media (max-width: 650px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ConditionGuide = styled.div`
  color: #999;
  font-size: 11px;
  line-height: 1.5;
`;

const ConditionActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 650px) {
    align-items: stretch;

    & > button {
      flex: 1;
    }
  }
`;

const ResetButton = styled.button`
  && {
    min-width: 72px;
    height: 36px;
    box-sizing: border-box;
    margin: 0;
    padding: 0 12px;
    border: 1px solid #dfe3e7;
    border-radius: 6px;
    appearance: none;
    background: #fff;
    color: #666;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  &&:hover:not(:disabled) {
    border-color: #f1d4c2;
    background-color: #fffaf6;
    color: #f5811f;
  }

  &&:disabled {
    background-color: #f5f5f5;
    color: #bbb;
    cursor: not-allowed;
  }
`;

const ApplyButton = styled.button`
  && {
    display: inline-flex;
    min-width: 72px;
    height: 36px;
    box-sizing: border-box;
    margin: 0;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 14px;
    border: 1px solid #f5811f;
    border-radius: 6px;
    appearance: none;
    background: #fff4e8;
    color: #f5811f;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
  }

  &&:hover:not(:disabled) {
    background-color: #ffe8d2;
  }

  &&:disabled {
    border-color: #ddd;
    background-color: #f5f5f5;
    color: #aaa;
    cursor: not-allowed;
  }
`;

const ButtonSpinner = styled.span`
  width: 12px;
  height: 12px;
  box-sizing: border-box;
  border: 2px solid #f3d5c0;
  border-top-color: #f5811f;
  border-radius: 50%;
  animation:
          user-search-spin
          0.7s linear infinite;

  @keyframes user-search-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ResultCard = styled.section`
  overflow: hidden;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 5px 18px rgba(31, 41, 55, 0.045);
`;

const ResultHeader = styled.header`
  display: flex;
  min-height: 74px;
  box-sizing: border-box;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 16px 20px;
  border-bottom: 1px solid #eeeeee;
  background-color: #f9fafb;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`;

const ResultTitleArea = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 11px;
`;

const ResultAccent = styled.span`
  width: 4px;
  height: 34px;
  flex: 0 0 4px;
  border-radius: 2px;
  background-color: #f5811f;
`;

const ResultTitle = styled.div`
  color: #222;
  font-size: 17px;
  font-weight: 700;
`;

const ResultTitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 5px;
`;

const ResultDescription = styled.div`
  color: #888;
  font-size: 12px;
  line-height: 1.5;
`;

const ResultCount = styled.div`
  display: inline-flex;
  min-height: 25px;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  padding: 0 9px;
  border: 1px solid #f1d4c2;
  border-radius: 13px;
  background-color: #fff4e8;

  & span {
    color: #888;
    font-size: 11px;
  }

  & strong {
    color: #f5811f;
    font-size: 13px;
  }
`;

const TableResultBox = styled.div`
  position: relative;
  min-height: 350px;
  margin: 16px 20px 20px;
  overflow: hidden;
  background-color: #fff;

  & > div {
    border: 0;
  }

  > section > div:last-child {
    border: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  .InovuaReactDataGrid,
  .inovua-react-toolkit-table {
    border: 0 !important;
    border-radius: 0;
    box-shadow: none;
  }

  @media (max-width: 700px) {
    margin: 14px;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  border-width: 0;
  gap: 9px;
  background-color:
          rgba(255, 255, 255, 0.78);
  color: #777;
  font-size: 13px;
  backdrop-filter: blur(1px);
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: 2px solid #f3d5c0;
  border-top-color: #f5811f;
  border-radius: 50%;
  animation:
          user-table-spin
          0.7s linear infinite;

  @keyframes user-table-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
